import { getCurrentScope, onScopeDispose, ref, watch, type Ref } from "vue";

// Анимация подсказки поля поиска: статичный текст → стирается → печатается
// пример запроса → пауза → стирается → статичный текст печатается обратно.
const IDLE_MS = 4000;
const ERASE_MS = 30;
const TYPE_MS = 60;
const HOLD_MS = 1800;

const prefersReducedMotion = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

export function useTypingPlaceholder(opts: {
  base: Ref<string>;
  examples: Ref<string[]>;
  paused: Ref<boolean>;
}): Ref<string> {
  const text = ref(opts.base.value);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let exampleIndex = 0;

  const stop = () => {
    if (timer != null) clearTimeout(timer);
    timer = null;
  };
  const later = (fn: () => void, ms: number) => {
    timer = setTimeout(fn, ms);
  };

  // Стереть text по символу, затем вызвать done.
  const erase = (done: () => void) => {
    if (!text.value) return done();
    text.value = text.value.slice(0, -1);
    later(() => erase(done), ERASE_MS);
  };
  // Напечатать target по символу, затем вызвать done.
  const type = (target: string, done: () => void) => {
    if (text.value.length >= target.length) return done();
    text.value = target.slice(0, text.value.length + 1);
    later(() => type(target, done), TYPE_MS);
  };

  const cycle = () => {
    const examples = opts.examples.value;
    if (!examples.length) return;
    const example = examples[exampleIndex++ % examples.length];
    erase(() =>
      type(example, () =>
        later(
          () =>
            erase(() =>
              type(opts.base.value, () => later(cycle, IDLE_MS)),
            ),
          HOLD_MS,
        ),
      ),
    );
  };

  const restart = () => {
    stop();
    text.value = opts.base.value;
    if (opts.paused.value || prefersReducedMotion()) return;
    later(cycle, IDLE_MS);
  };

  watch([opts.base, opts.examples, opts.paused], restart, { immediate: true, flush: "sync" });
  if (getCurrentScope()) onScopeDispose(stop);
  return text;
}
